/**
 * Slide-out Cart JavaScript
 *
 * This script handles the functionality of the slide-out cart including:
 * - Opening and closing the cart
 * - Updating item quantities
 * - Removing items
 * - AJAX cart updates
 */

 class SlideOutCart {
    constructor() {
      this.cart = document.getElementById("slide-out-cart");
      this.overlay = document.getElementById("slide-out-cart-overlay");
      this.cartForm = document.getElementById("cart-form");
      this.cartItems = this.cart?.querySelector(".slide-out-cart__items");
      this.isUpdating = false;
  
      this.init();
    }
  
    init() {
      // Initialize cart toggle buttons
      this.initCartToggle();
  
      // Initialize quantity buttons
      this.initQuantityButtons();
  
      // Initialize remove item buttons
      this.initRemoveButtons();
  
      // Initialize cart form submission
      this.initCartForm();

      this.initBestSellers();
  
      // Listen for cart changes from other parts of the site
      document.addEventListener("cart:updated", this.updateCart.bind(this));
      this.fetchAndUpdateCart();
    }
  
    initCartToggle() {
      // Add click event to cart toggle buttons (usually in the header)
      const cartToggleButtons = document.querySelectorAll("[data-cart-toggle]");
      cartToggleButtons.forEach((button) => {
        button.addEventListener("click", this.toggleCart.bind(this));
      });
  
      // Close cart when clicking the close button
      const closeButton = this.cart?.querySelector(".slide-out-cart__close");
      if (closeButton) {
        closeButton.addEventListener("click", this.closeCart.bind(this));
      }
  
      // Close cart when clicking the overlay
      if (this.overlay) {
        this.overlay.addEventListener("click", this.closeCart.bind(this));
      }
  
      // Close cart when pressing ESC key
      document.addEventListener("keydown", (event) => {
        if (event.key === "Escape") {
          this.closeCart();
        }
      });
    }
  
    initQuantityButtons() {
      // Quantity increase buttons
      const plusButtons = this.cart?.querySelectorAll("[data-quantity-plus]");
      plusButtons?.forEach((button) => {
        button.addEventListener("click", this.increaseQuantity.bind(this));
      });
  
      // Quantity decrease buttons
      const minusButtons = this.cart?.querySelectorAll("[data-quantity-minus]");
      minusButtons?.forEach((button) => {
        button.addEventListener("click", this.decreaseQuantity.bind(this));
      });
  
      // Quantity input changes
      const quantityInputs = this.cart?.querySelectorAll("[data-quantity-input]");
      quantityInputs?.forEach((input) => {
        input.addEventListener("change", this.quantityChanged.bind(this));
      });
    }
  
    initRemoveButtons() {
      const removeButtons = this.cart?.querySelectorAll("[data-remove-item]");
      removeButtons?.forEach((button) => {
        button.addEventListener("click", this.removeItem.bind(this));
      });
    }
  
    initCartForm() {
      if (this.cartForm) {
        this.cartForm.addEventListener("submit", this.handleFormSubmit.bind(this));
      }
    }
  
    toggleCart(event) {
      if (event) {
        event.preventDefault();
      }
  
      if (this.cart?.classList.contains("is-active")) {
        this.closeCart();
      } else {
        this.openCart();
        // Fetch and update cart products when opening the cart
        this.fetchAndUpdateCart();
      }
    }
  
    openCart() {
      this.cart?.classList.add("is-active");
      this.overlay?.classList.add("is-active");
      document.body.classList.add("slide-out-cart-open");
  
      // Add loading state while fetching cart data
      this.cart?.classList.add("is-loading");
    }
  
    closeCart() {
      this.cart?.classList.remove("is-active");
      this.overlay?.classList.remove("is-active");
      document.body.classList.remove("slide-out-cart-open");
    }
  
    increaseQuantity(event) {
      const button = event.currentTarget;
      const wrapper = button.closest(".slide-out-cart__item-quantity");
      const input = wrapper?.querySelector("[data-quantity-input]");
  
      if (input) {
        const currentValue = parseInt(input.value, 10);
        input.value = currentValue + 1;
        this.updateItemQuantity(input);
      }
    }
  
    decreaseQuantity(event) {
      const button = event.currentTarget;
      const wrapper = button.closest(".slide-out-cart__item-quantity");
      const input = wrapper?.querySelector("[data-quantity-input]");
  
      if (input) {
        const currentValue = parseInt(input.value, 10);
        if (currentValue > 0) {
          input.value = currentValue - 1;
          this.updateItemQuantity(input);
        }
      }
    }
  
    quantityChanged(event) {
      const input = event.currentTarget;
      this.updateItemQuantity(input);
    }
  
    updateItemQuantity(input) {
      if (this.isUpdating) return;
  
      const newQuantity = parseInt(input.value, 10);
      const itemKey = input.closest("[data-cart-item]")?.dataset.lineItemKey;
  
      if (itemKey) {
        this.isUpdating = true;
        this.cart?.classList.add("is-loading");
  
        const formData = new FormData();
        formData.append("updates[" + itemKey + "]", newQuantity);
        formData.append("sections", "slide-out-cart");
  
        fetch(window.Shopify.routes.root + "cart/update.js", {
          method: "POST",
          body: formData,
        })
          .then((response) => response.json())
          .then((data) => {
            this.updateCartFromResponse(data);
          })
          .catch((error) => {
            console.error("Error updating cart:", error);
          })
          .finally(() => {
            this.isUpdating = false;
            this.cart?.classList.remove("is-loading");
          });
      }
    }
  
    removeItem(event) {
      const button = event.currentTarget;
      const item = button.closest("[data-cart-item]");
      const itemKey = item?.dataset.lineItemKey;
  
      if (itemKey) {
        this.isUpdating = true;
        this.cart?.classList.add("is-loading");
  
        const formData = new FormData();
        formData.append("updates[" + itemKey + "]", 0);
        formData.append("sections", "slide-out-cart");
  
        fetch(window.Shopify.routes.root + "cart/update.js", {
          method: "POST",
          body: formData,
        })
          .then((response) => response.json())
          .then((data) => {
            this.updateCartFromResponse(data);
          })
          .catch((error) => {
            console.error("Error removing item:", error);
          })
          .finally(() => {
            this.isUpdating = false;
            this.cart?.classList.remove("is-loading");
          });
      }
    }
  
    handleFormSubmit(event) {
      // Only handle form submission if it's not a checkout
      if (!event.submitter || event.submitter.name !== "checkout") {
        event.preventDefault();
  
        this.isUpdating = true;
        this.cart?.classList.add("is-loading");
  
        const formData = new FormData(this.cartForm);
        formData.append("sections", "slide-out-cart");
  
        fetch(window.Shopify.routes.root + "cart/update.js", {
          method: "POST",
          body: formData,
        })
          .then((response) => response.json())
          .then((data) => {
            this.updateCartFromResponse(data);
          })
          .catch((error) => {
            console.error("Error updating cart:", error);
          })
          .finally(() => {
            this.isUpdating = false;
            this.cart?.classList.remove("is-loading");
          });
      }
    }
  
    updateCartFromResponse(data) {
      // Update cart count in the header
      this.updateCartCount(data.item_count);
  
      // If the cart is empty, refresh the page to show empty cart state
      if (data.item_count === 0) {
        window.location.reload();
        return;
      }
  
      // Check if sections data is available
      if (data.sections && data.sections["slide-out-cart"]) {
        // Process sections data if available
        const parser = new DOMParser();
        const newCartHTML = parser.parseFromString(data.sections["slide-out-cart"], "text/html");
        const newCartItems = newCartHTML.querySelector(".slide-out-cart__items");
        const newCartFooter = newCartHTML.querySelector(".slide-out-cart__footer");
  
        if (newCartItems && this.cartItems) {
          this.cartItems.innerHTML = newCartItems.innerHTML;
        }
  
        const cartFooter = this.cart?.querySelector(".slide-out-cart__footer");
        if (newCartFooter && cartFooter) {
          cartFooter.innerHTML = newCartFooter.innerHTML;
        }
  
        // Reinitialize event listeners for the new elements
        this.initQuantityButtons();
        this.initRemoveButtons();
      } else {
        // If sections data is not available, fetch the cart data and update manually
        this.fetchAndUpdateCart();
      }
    }
  
    fetchAndUpdateCart() {
      // Set loading state
      this.isUpdating = true;
  
      fetch(window.Shopify.routes.root + "cart.js")
        .then((response) => response.json())
        .then((cartData) => {
          // Check if all items contain "Offer" in their title
          this.checkAndRemoveOfferItems(cartData).then(updatedCartData => {
            // Update cart items based on the cart data
            this.updateCartItems(updatedCartData || cartData);
  
            // After updating cart items, update the upsell products
            this.updateUpsellProducts();
          });
        })
        .catch((error) => {
          console.error("Error fetching cart data:", error);
          // Remove loading state even if there's an error
          this.cart?.classList.remove("is-loading");
        })
        .finally(() => {
          // Reset updating flag
          this.isUpdating = false;
        });
    }
  
    checkAndRemoveOfferItems(cartData) {
      // If no items, just return the original data
      if (!cartData.items || cartData.items.length === 0) {
        return Promise.resolve(cartData);
      }
  
      // Check if all items contain "Offer" in their title
      const hasNonOfferItems = cartData.items.some(item =>
        !item.product_title.toLowerCase().includes("offer")
      );
  
      // If there are non-offer items, no need to remove anything
      if (hasNonOfferItems) {
        console.log("Cart has non-offer items, keeping all items");
        return Promise.resolve(cartData);
      }
  
      // If all items are offers, remove them
      console.log("All items are offers, removing them");
  
      // Create updates object with all items set to quantity 0
      const updates = {};
      cartData.items.forEach(item => {
        updates[item.key] = 0;
      });
  
      // Send request to update cart (remove all items)
      return fetch(window.Shopify.routes.root + "cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ updates }),
      })
        .then(response => response.json())
        .then(updatedCart => {
          console.log("All offer items removed from cart");
          return updatedCart;
        })
        .catch(error => {
          console.error("Error removing offer items:", error);
          return cartData; // Return original data if there's an error
        });
    }
  
    updateUpsellProducts() {
      var holder = document.querySelector(".slide-out-cart__footer-upsells");

      if (!holder) return;
  
      // Get related products from the slider
      var relatedProducts = document.querySelectorAll("#related-products-slider li.splide__slide:not(.splide__slide--clone)");
      var htmlString = "";
  
      // If we're on a product template page, use related products
      if (document.body.classList.contains("template-product") && relatedProducts.length > 0  && relatedProducts.length < 4) {
        relatedProducts.forEach((product) => {
          try {
            var url = product.querySelector("a").href,
              imageSrc = product.querySelector("a img").src,
              name = product.querySelector("h3").innerText,
              productId = product.dataset.id,
              upsellID = BetterVits.getDiscountedProductVariant[BetterVits.getDiscountedProductID[productId]],
              price = BetterVits.returnUpsellPrice(upsellID);
  
              htmlString += `

                    <div class="slide-out-cart__item splide__slide" data-cart-item="" data-line-item-key="${upsellID}">
                        <div class="slide-out-cart__item-image">
                            <a href="${url}">
                                <img src="${imageSrc}" alt="${name}">
                            </a>
                        </div>
                        <div class="slide-out-cart__item-details">
                            <h3 class="slide-out-cart__item-title">
                                <a href="${url}">${name}</a>
                            </h3>
                            <div class="slide-out-cart__item-price">
                                $${price}
                            </div>
                            <a href="#" class="button primary upsellAdder" data-upsell-adder data-added="false"><span>ADD TO CART</span></a>
                        </div>
                    </div>`;
          } catch (e) {
            console.error("Error processing related product:", e);
          }
        });
      } else {
        // Default upsell products if not on a product page or no related products
        htmlString = `<div class="slide-out-cart__item splide__slide" data-cart-item="" data-line-item-key="45005138166068">
                        <div class="slide-out-cart__item-image">
                            <a href="/products/vitamin-d3-k2">
                                <img src="https://cdn.shopify.com/s/files/1/0760/1618/9748/files/vit-d-shop.jpg?v=1715256383" alt="Vitamin D3 & K2">
                            </a>
                        </div>
                        <div class="slide-out-cart__item-details">
                            <h3 class="slide-out-cart__item-title">
                                <a href="/products/vitamin-d3-k2">Vitamin D3 & K2</a>
                            </h3>
                            <div class="slide-out-cart__item-price">
                                $25.00
                            </div>

                            <div class="slide-out-cart__item-subscription" >
                              <div class="subscription-label">One-time purchase</div>
                              <label class="subscription-checkbox">
                                <input type="checkbox" class="subscription-toggle" data-variant-id="" data-line-key="">
                                <span class="checkmark"></span>
                                    Subscribe and Save 10%
                              </label>
                            </div>

                            <a href="#" class="button primary upsellAdder" data-upsell-adder data-added="false"><span>ADD TO CART</span></a>
                        </div>
                    </div>
                    <div class="slide-out-cart__item splide__slide" data-cart-item="" data-line-item-key="45005138395444">
                        <div class="slide-out-cart__item-image">
                            <a href="/products/probiotic-complex">
                                <img src="https://cdn.shopify.com/s/files/1/0760/1618/9748/files/probiotic-us-shop.jpg?v=1683203699" alt="Probiotic">
                            </a>
                        </div>
                        <div class="slide-out-cart__item-details">
                            <h3 class="slide-out-cart__item-title">
                                <a href="/products/probiotic-complex">Probiotic</a>
                            </h3>
                            <div class="slide-out-cart__item-price">
                                $25.00
                            </div>

                            <div class="slide-out-cart__item-subscription" >
                                <div class="subscription-label">One-time purchase</div>
                                <label class="subscription-checkbox">
                                <input type="checkbox" class="subscription-toggle" data-variant-id="" data-line-key="">
                                <span class="checkmark"></span>
                                    Subscribe and Save 10%
                                </label>
                            </div>
                            
                            <a href="#" class="button primary upsellAdder" data-upsell-adder data-added="false"><span>ADD TO CART</span></a>
                        </div>
                    </div>`;
      }
  
      // Update the holder with the new HTML
      holder.innerHTML = `
            <div class="splide__track">
                <div class="splide__list">
                    ${htmlString}
                </div>
            </div>
    `;

    var slider = new Splide(".slide-out-cart__footer-upsells", {
        type: "slide",
        perPage: 1,
        gap: 0,
        speed: 700,
        easing: "cubic-bezier(0.55, 0.24, 0.52, 1.0)",
        autoplay: false,
        interval: 600000,
        padding: 0,
        updateOnMove: true,
        pagination: false,
        arrows: true,
        arrowPath:
          "M27.7693 5.1C28.1007 5.1 28.3693 4.83137 28.3693 4.5C28.3693 4.16863 28.1007 3.9 27.7693 3.9V4.5V5.1ZM0.345022 4.07574C0.110708 4.31005 0.110708 4.68995 0.345022 4.92426L4.1634 8.74264C4.39771 8.97696 4.77761 8.97696 5.01193 8.74264C5.24624 8.50833 5.24624 8.12843 5.01193 7.89411L1.61782 4.5L5.01193 1.10589C5.24624 0.871573 5.24624 0.491674 5.01193 0.257359C4.77761 0.0230446 4.39771 0.0230446 4.1634 0.257359L0.345022 4.07574ZM27.7693 4.5V3.9L0.769287 3.9V4.5V5.1L27.7693 5.1V4.5Z",
        breakpoints: {
          767: {
            perPage: 1,
          },
          1024: {
            perPage: 1,
          },
        },
      }).mount();
  
      // Add event listeners to the upsell adder buttons
      document.querySelectorAll("[data-upsell-adder]").forEach((adder) => {
        adder.addEventListener("click", function () {
          var variantID = adder.closest(".slide-out-cart__item").dataset.lineItemKey;
          var buttonSpan = adder.querySelector("span");
          var isAdded = adder.dataset.added === "true";

          var isSubscription = adder.closest(".slide-out-cart__item").querySelector('.subscription-toggle').checked
          var sellingPlan = window.BetterVits?.offersSellingPlans?.[variantID][0];
  
          // Get the price from the item
          var priceElement = adder.closest(".slide-out-cart__item-details").querySelector(".slide-out-cart__item-price");
          var priceText = priceElement.textContent.trim();
  
          // Extract the numeric price (remove currency symbol and any whitespace)
          var price = parseFloat(priceText.replace(/[^0-9.]/g, ''));
  
          // Update the subtotal immediately
          var subtotalElement = document.querySelector(".slide-out-cart__subtotal span:last-child");
  
          if (isAdded) {
            // Item is already added, so remove it
            adder.dataset.added = "false";
            buttonSpan.innerText = "ADD";
  
            // Subtract price from subtotal
            if (subtotalElement) {
              var currentSubtotal = parseFloat(subtotalElement.textContent.replace(/[^0-9.]/g, ''));
              var newSubtotal = currentSubtotal - price;
  
              // Format the new subtotal with the same currency format
              const formatter = new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              });
  
              subtotalElement.textContent = formatter.format(newSubtotal);
            }
  
            // Send the AJAX request to remove the item from the cart
            var xhr = new XMLHttpRequest();
            xhr.onreadystatechange = function () {
              if (xhr.readyState == 4 && xhr.status == 200) {
                // Dispatch event to update the cart
                document.dispatchEvent(new CustomEvent("cart:updated"));
              }
            };
            xhr.open("POST", "/cart/change.js", true);
            xhr.setRequestHeader("Content-Type", "application/json");
  
            xhr.send(
              JSON.stringify({
                id: variantID,
                quantity: 0
              })
            );
          } else {
            // Item is not added, so add it
            adder.dataset.added = "true";
            buttonSpan.innerText = "Added";
  
            // Add price to subtotal
            if (subtotalElement) {
              var currentSubtotal = parseFloat(subtotalElement.textContent.replace(/[^0-9.]/g, ''));
              var newSubtotal = currentSubtotal + price;
  
              // Format the new subtotal with the same currency format
              const formatter = new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              });
  
              subtotalElement.textContent = formatter.format(newSubtotal);
            }
  
            // Send the AJAX request to add the item to the cart
            var xhr = new XMLHttpRequest();
            xhr.onreadystatechange = function () {
              if (xhr.readyState == 4 && xhr.status == 200) {
                // Dispatch event to update the cart
                document.dispatchEvent(new CustomEvent("cart:updated"));
              }
            };
            xhr.open("POST", "/cart/add.js", true);
            xhr.setRequestHeader("Content-Type", "application/json");
  
            xhr.send(
              JSON.stringify({
                attributes: {
                  Parent: variantID,
                },
                items: [
                  {
                    quantity: 1,
                    id: variantID,
                    ...(sellingPlan && isSubscription && { selling_plan: sellingPlan }),
                    properties: {},
                  },
                ],
              })
            );
          }
        });
      });
    }
  
    updateCartItems(cartData) {
      // Clear loading state
      this.cart?.classList.remove("is-loading");
  
      // Get the cart body element
      const cartBody = this.cart?.querySelector(".slide-out-cart__body");
      if (!cartBody) return;
  
      // Check if cart has items
      if (cartData.items && cartData.items.length > 0) {
        console.log("Cart has items:", cartData.items.length);
  
        // Create cart form with items if it doesn't exist
  
          cartBody.innerHTML = `
            <form action="${window.Shopify.routes.root}cart" method="post" id="cart-form">
              <div class="slide-out-cart__items"></div>
              <div class="slide-out-cart__footer"></div>
            </form>
          `;
          this.cartForm = document.getElementById("cart-form");
          this.cartItems = this.cart?.querySelector(".slide-out-cart__items");
  
  
        // Create HTML for cart items
        let itemsHTML = "";
  
        cartData.items.forEach((item) => {
          const itemPrice = this.formatMoney(item.final_price);
          const originalPrice = item.original_price > item.final_price ? `<s>${this.formatMoney(item.original_price)}</s>` : "";
  
          const variantTitle = item.variant_title ? `<p class="slide-out-cart__item-variant">${item.variant_title}</p>` : "";
  
          itemsHTML += `
            <div class="slide-out-cart__item" data-cart-item data-line-item-key="${item.key}">
              <div class="slide-out-cart__item-image">
                <a href="${item.url}">
                  <img src="${item.image}" alt="${item.title}">
                </a>
              </div>
              <div class="slide-out-cart__item-details">
                <h3 class="slide-out-cart__item-title">
                  <a href="${item.url}">${item.product_title}</a>
                </h3>
                ${variantTitle}
                <div class="slide-out-cart__item-price">
                  ${itemPrice}
                  ${originalPrice}
                </div>
  
                <div class="slide-out-cart__item-quantity" ${item.product_title.toLowerCase().indexOf("offer") > -1 ? "style='display:none'" : ""}>
                  <button type="button" class="quantity-button quantity-down" data-quantity-minus>-</button>
                  <input type="number" name="updates[]" id="updates_${item.key}" value="${item.quantity}" min="0" pattern="[0-9]*" data-quantity-input>
                  <button type="button" class="quantity-button quantity-up" data-quantity-plus>+</button>
                </div>

                ${!item.selling_plan_allocation ? `
                <div class="slide-out-cart__item-subscription" ${item.product_title.toLowerCase().indexOf("offer") > -1 ? "style='display:none'" : ""}>
                  <div class="subscription-label">One-time purchase</div>
                  <label class="subscription-checkbox">
                    <input type="checkbox" class="subscription-toggle" data-variant-id="${item.variant_id}" data-line-key="${item.key}">
                    <span class="checkmark"></span>
                    Subscribe and Save 10%
                  </label>
                </div>
                ` : `
                <div class="slide-out-cart__item-subscription" ${item.product_title.toLowerCase().indexOf("offer") > -1 ? "" : ""}>
                  <div class="subscription-label">${item.selling_plan_allocation.selling_plan.name}</div>
                </div>
                `}

                <button type="button" class="slide-out-cart__item-remove" data-remove-item>
                  <svg xmlns="http://www.w3.org/2000/svg" width="11" height="15" viewBox="0 0 11 15" fill="none">
                    <path d="M10.436 3.75V12.5C10.436 12.8315 10.2955 13.1495 10.0455 13.3839C9.79542 13.6183 9.45628 13.75 9.10266 13.75H2.43599C2.08237 13.75 1.74323 13.6183 1.49319 13.3839C1.24314 13.1495 1.10266 12.8315 1.10266 12.5V3.75M3.10266 3.75V2.5C3.10266 2.16848 3.24314 1.85054 3.49319 1.61612C3.74323 1.3817 4.08237 1.25 4.43599 1.25H7.10266C7.45628 1.25 7.79542 1.3817 8.04547 1.61612C8.29552 1.85054 8.43599 2.16848 8.43599 2.5V3.75" stroke="#003E52" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>

              </div>
            </div>
          `;
        });
  
        // Update cart items
        if (this.cartItems) {
          this.cartItems.innerHTML = itemsHTML;
        }
  
        // Update cart footer
        const cartFooter = this.cart?.querySelector(".slide-out-cart__footer");
        if (cartFooter) {
          const subtotalPrice = this.formatMoney(cartData.total_price);
  
          let discountsHTML = "";
          if (cartData.cart_level_discount_applications && cartData.cart_level_discount_applications.length > 0) {
            discountsHTML = '<div class="slide-out-cart__discounts">';
            cartData.cart_level_discount_applications.forEach((discount) => {
              discountsHTML += `
                <div class="slide-out-cart__discount">
                  <span>${discount.title}</span>
                  <span>-${this.formatMoney(discount.total_allocated_amount)}</span>
                </div>
              `;
            });
            discountsHTML += "</div>";
          }
  
          cartFooter.innerHTML = `
          <h2 class="footer-heading">Pairs Well With</h2>
          <div class="slide-out-cart__footer-upsells splide"></div>
            <div class="slide-out-cart__shipping">
                <span>Shipping</span>
                <span>Calculated at Checkout</span>
            </div>
            <div class="slide-out-cart__subtotal">
              <span>Total</span>
              <span>${subtotalPrice}</span>
            </div>
            ${discountsHTML}
            <div class="slide-out-cart__buttons">
              <button type="submit" name="update" class="slide-out-cart__update" style="display:none">Update Cart</button>
              <button type="submit" name="checkout" class="button primary"><span>Checkout</span></button>
            </div>
          `;
        }
  
        // Reinitialize event listeners for the new elements
        this.initQuantityButtons();
        this.initRemoveButtons();
        this.initCartForm();
        this.initSubscriptionToggles();
      } else {
        // Show empty cart message
        console.log("Cart is empty");
        // 
        cartBody.innerHTML = `
          <form action="${window.Shopify.routes.root}cart" method="post" id="cart-form">
            <div class="slide-out-cart__empty">
              <p class="slide-out-cart__empty-title"><span>Almost there!</span> Add some goodness to your cart.</p>
              <p class="slide-out-cart__empty-header">Best Sellers:</p>
              <div class="slide-out-cart__best-sellers">
                <div class="slide-out-cart__item" data-cart-item="" data-line-item-key="45005138395444">
                    <div class="slide-out-cart__item-image">
                        <a href="/products/probiotic-complex">
                            <img src="https://cdn.shopify.com/s/files/1/0760/1618/9748/files/probiotic-us-shop.jpg?v=1683203699" alt="Probiotic">
                        </a>
                    </div>
                    <div class="slide-out-cart__item-details">
                        <h3 class="slide-out-cart__item-title">
                            <a href="/products/probiotic-complex">Probiotic</a>
                        </h3>
                        <div class="slide-out-cart__item-price">
                            $40.00
                        </div>

                        <div class="slide-out-cart__item-subscription">
                            <div class="subscription-label">One-time purchase</div>
                            <label class="subscription-checkbox">
                                <input type="checkbox" class="subscription-toggle" data-variant-id="45005138592052">
                                <span class="checkmark"></span>
                                Subscribe and Save 10%
                            </label>
                        </div>

                        <a href="#" class="button primary" data-add-best-seller="true" data-variant-id="45005138592052"><span>ADD</span></a>
                    </div>
                </div>

                <div class="slide-out-cart__item" data-cart-item="" data-line-item-key="45005138395444">
                    <div class="slide-out-cart__item-image">
                        <a href="/products/glucosamine-complex">
                            <img src="https://cdn.shopify.com/s/files/1/0760/1618/9748/files/glucosamine-us-shop.jpg?v=1683203699" alt="Glucosamine Complex">
                        </a>
                    </div>
                    <div class="slide-out-cart__item-details">
                        <h3 class="slide-out-cart__item-title">
                            <a href="/products/glucosamine-complex">Glucosamine Complex</a>
                        </h3>
                        <div class="slide-out-cart__item-price">
                            $40.00
                        </div>

                        <div class="slide-out-cart__item-subscription">
                            <div class="subscription-label">One-time purchase</div>
                            <label class="subscription-checkbox">
                                <input type="checkbox" class="subscription-toggle" data-variant-id="45005138428212">
                                <span class="checkmark"></span>
                                Subscribe and Save 10%
                            </label>
                        </div>

                        <a href="/shop" class="button primary" data-add-best-seller="true" data-variant-id="45005138428212"><span>ADD</span></a>
                    </div>
                </div>
                

              </div>

              <div class="slide-out-cart__empty-footer">
               <a href="/pages/shop" class="button light" style="justify-content: center;"><span>SHOP BEST SELLERS</span></a>

                <button type="submit" name="update" class="slide-out-cart__update" style="display:none">Update Cart</button>
              </div>
            </div>
          </form>
        `;
  
        // Update references
        this.cartForm = document.getElementById("cart-form");
        this.cartItems = null;
        this.initBestSellers();
      }
    }
  
    formatMoney(cents) {
      // Simple money formatter - can be replaced with Shopify's Currency.formatMoney if available
      const formatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      });
  
      return formatter.format(cents / 100);
    }
  
    updateCartCount(count) {
      // Update cart count in the header
      const cartCountElements = document.querySelectorAll("[data-cart-count]");
      cartCountElements.forEach((element) => {
        element.textContent = count;
  
        // Toggle empty class
        if (count === 0) {
          element.classList.add("is-empty");
        } else {
          element.classList.remove("is-empty");
        }
      });
    }

    initSubscriptionToggles() {
        const subscriptionToggles = this.cart?.querySelectorAll(".subscription-toggle");
        subscriptionToggles?.forEach((toggle) => {
            toggle.addEventListener("change", this.handleSubscriptionToggle.bind(this));
        });
    }

    addSubscriptionPlan(quantity, variantId, sellingPlan) {
        return fetch(window.Shopify.routes.root + "cart/add.js", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                items: [
                    {
                        quantity: quantity,
                        id: variantId,
                        selling_plan: sellingPlan,
                    },
                ],
            }),
        })
        .then(res => res.json())
        .then(data => { 
            console.log("Successfully converted to subscription:");
            this.fetchAndUpdateCart();
        })
    }

    handleSubscriptionToggle(event) {
        const checkbox = event.currentTarget;
        const variantId = parseInt(checkbox.dataset.variantId);
        const lineKey = checkbox.dataset.lineKey;
        const cartItem = checkbox.closest("[data-cart-item]");
        const quantityInput = cartItem?.querySelector("[data-quantity-input]");
        const quantity = quantityInput ? parseInt(quantityInput.value) : 1;
    
        if (checkbox.checked) {
            console.log("Converting to subscription:", variantId);
        
            // Check if we have selling plans for this variant
            const sellingPlans = window.BetterVits?.productsSelingPlans?.[variantId][0];
        
            if (!sellingPlans || sellingPlans.length === 0) {
                console.error("No selling plans found for variant:", variantId);
                checkbox.checked = false;
                alert("Subscription not available for this product.");
                return;
            }
        
            // Use the first selling plan (30-day subscription)
            // console.log("selling plan", sellingPlans)
            const sellingPlan = sellingPlans;
        
            this.isUpdating = true;
            this.cart?.classList.add("is-loading");
        
            // First remove the current item using the line key
            fetch(window.Shopify.routes.root + "cart/update.js", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    updates: {
                        [lineKey]: 0
                    }
                }),
            })
            .then((response) => { 
                // response.json() 
                return this.addSubscriptionPlan(quantity, variantId, sellingPlan)
            })
            .catch((error) => {
                console.error("Error converting to subscription:", error);
                checkbox.checked = false;
                alert("Failed to convert to subscription. Please try again.", error);
            })
            .finally(() => {
                this.isUpdating = false;
                this.cart?.classList.remove("is-loading");
            });
        }
    }

    initBestSellers() {
        const bestSellers = this.cart?.querySelectorAll("[data-add-best-seller]");
        console.log(bestSellers)
        bestSellers?.forEach((button) => {
            console.log('test', button)
            button.addEventListener("click", this.handleAddBestSeller.bind(this));
        });
    }

    handleAddBestSeller(event) {
        event.preventDefault();
        console.log(event, "Best Sellers", event.currentTarget.dataset.variantId);
        const variantId = parseInt(event.currentTarget.dataset.variantId);
        let isSubscription = false

        // Check if we have selling plans for this variant
        const sellingPlan = window.BetterVits?.productsSelingPlans?.[variantId][0];
        const checkbox = document.querySelector(`.subscription-toggle[data-variant-id="${variantId}"]`);

        if(checkbox.checked) {
            isSubscription = true
        }
        
        fetch(window.Shopify.routes.root + "cart/add.js", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                items: [
                    {
                        quantity: 1,
                        id: variantId,
                        // selling_plan: sellingPlan,
                        ...(sellingPlan && isSubscription && { selling_plan: sellingPlan }),
                    },
                ],
            }),
        })
        .then(res => res.json())
        .then(data => { 
            console.log("Successfully added best seller:");
            this.fetchAndUpdateCart();
        })
    }
  
    updateCart() {
      // Fetch the latest cart data
      fetch(window.Shopify.routes.root + "cart.js")
        .then((response) => response.json())
        .then((data) => {
          this.updateCartCount(data.item_count);
        })
        .catch((error) => {
          console.error("Error fetching cart:", error);
        });
    }
  }
  
  // Initialize the slide-out cart when the DOM is fully loaded
  document.addEventListener("DOMContentLoaded", () => {
    new SlideOutCart();
  });
  
  // Add to cart AJAX handler
  function addToCartAjax(form) {
    const submitButton = form.querySelector('[type="submit"]');
    const originalButtonText = submitButton.innerHTML;
  
    submitButton.disabled = true;
    submitButton.innerHTML = "Adding...";
  
    fetch(window.Shopify.routes.root + "cart/add.js", {
      method: "POST",
      body: new FormData(form),
    })
      .then((response) => response.json())
      .then((data) => {
        // If there was an error, show it
        if (data.status && data.status !== 200) {
          throw new Error(data.description || "Error adding item to cart");
        }
  
        // Dispatch event to update the cart
        document.dispatchEvent(new CustomEvent("cart:updated"));
  
        // Open the slide-out cart
        const cart = document.getElementById("slide-out-cart");
        if (cart && !cart.classList.contains("is-active")) {
          const cartInstance = new SlideOutCart();
          cartInstance.openCart();
        }
      })
      .catch((error) => {
        console.error("Error adding to cart:", error);
        alert(error.message || "There was an error adding this item to your cart. Please try again.");
      })
      .finally(() => {
        submitButton.disabled = false;
        submitButton.innerHTML = originalButtonText;
      });
  
    return false;
  }
  
  // Add event listeners to all add to cart forms
  document.addEventListener("DOMContentLoaded", () => {
    const addToCartForms = document.querySelectorAll('form[action="/cart/add"]');
    addToCartForms.forEach((form) => {
      form.addEventListener("submit", function (event) {
        event.preventDefault();
        addToCartAjax(this);
      });
    });
  });
  